import React, { useRef, useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, Environment, Lightformer } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as THREE from 'three';
import styles from './PreloadedLandingV2.module.css';
import { logEvent } from '../../analytics';
import { saveStateBeforeNavigation } from '../../store/reducers/progressionReducer';

const SECTION_KEYS = ['hero', 's1', 's2', 's3', 's4', 's5', 's6', 's7'];

const WHY_IT_MATTERS = [
  'Swiss Made',
  'Preloaded',
  'New Delivery System',
  'Optimized for Low-Light Vision',
  'True-to-Life Vision',
  'EDOF * Technology'
];

// Portail d'entree SAV-IOL. Le fragment #event ouvre directement le formulaire
// d'inscription ; sans lui le visiteur atterrit sur le choix des trois parcours.
const EVENT_FORM_URL = 'https://join.sav-iol.com/#event';

// Les quatre moments partagent le meme cadrage : seule la lumiere change.
const DAY_NIGHT_STOPS = [
  { id: 'day', label: 'Day', className: 'layerDay', image: 'day.jpg' },
  { id: 'golden', label: 'Golden hour', className: 'layerGolden', image: 'golden-hour.jpg' },
  { id: 'dusk', label: 'Dusk', className: 'layerDusk', image: 'dusk.jpg' },
  { id: 'night', label: 'Night', className: 'layerNight', image: 'night.jpg' }
];

// Les visuels de /public/lp2/ sont passes en variable CSS : le degrade de repli
// declare dans la feuille de style prend le relais tant qu'un fichier manque.
const mediaImage = (filename) => ({ '--lp2-image': `url('/lp2/${filename}')` });

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Progression d'une valeur sur un intervalle, ramenee entre 0 et 1.
const range = (value, start, end) => clamp((value - start) / (end - start), 0, 1);

// Apparition puis disparition d'un bloc de texte au fil de sa section.
const reveal = (p) => range(p, 0.12, 0.38) * (1 - range(p, 0.72, 0.98));

const easeOut = (p) => 1 - Math.pow(1 - p, 3);

// Depart et arrivee adoucis, pour une montee sans a-coup.
const smoothstep = (p) => p * p * (3 - 2 * p);

function EmergingObject({ progressRef }) {
  const groupRef = useRef();
  const ambientRef = useRef();
  const grazingLightRef = useRef();
  const rimLightRef = useRef();
  const materialsRef = useRef([]);
  const { scene } = useGLTF('/lentilles.glb');

  // Le GLB aligne quatre lentilles. On n'en garde qu'une, cadree assez pres pour
  // deborder du champ : on n'apercoit qu'un bord, jamais l'objet entier.
  const model = useMemo(() => {
    const clone = scene.clone(true);
    const meshes = [];

    clone.traverse((child) => {
      if (child.isMesh) {
        meshes.push(child);
      }
    });

    materialsRef.current = [];
    meshes.forEach((mesh, index) => {
      mesh.visible = index === 0;
      mesh.material = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color('#3c4756'),
        metalness: 0,
        roughness: 0.18,
        clearcoat: 1,
        clearcoatRoughness: 0.08,
        ior: 1.5,
        specularIntensity: 1,
        // Valeur de depart : l'intensite reelle est pilotee au scroll.
        envMapIntensity: 0
      });
      materialsRef.current.push(mesh.material);
    });

    const box = new THREE.Box3().setFromObject(meshes[0] ?? clone);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z) || 1;

    clone.position.sub(center);

    return { object: clone, fit: 5.6 / maxDimension };
  }, [scene]);

  useFrame((state) => {
    const p = progressRef.current;
    const time = state.clock.getElapsedTime();

    // L'objet emerge de l'obscurite : la lumiere monte tres lentement avec le
    // scroll, puis retombe pour laisser le noir se refermer avant la section 4.
    const emerge =
      smoothstep(range(p, 0.08, 0.62)) * (1 - smoothstep(range(p, 0.86, 1)));

    if (ambientRef.current) {
      ambientRef.current.intensity = emerge * 0.45;
    }
    if (grazingLightRef.current) {
      grazingLightRef.current.intensity = 2 + emerge * 158;
    }
    if (rimLightRef.current) {
      rimLightRef.current.intensity = 1 + emerge * 89;
    }
    materialsRef.current.forEach((material) => {
      // Les reflets sur les aretes se devinent avant que le volume se revele.
      material.envMapIntensity = 0.12 + emerge * 3.5;
    });

    if (groupRef.current) {
      // Rotation tres lente : l'objet ne se devoile jamais entierement.
      groupRef.current.rotation.y = -0.9 + p * 1.6;
      groupRef.current.rotation.x = 0.34 + Math.sin(time * 0.12) * 0.06;
      groupRef.current.rotation.z = -0.25 + Math.sin(time * 0.09) * 0.04;
      // Le cadrage derive lentement d'un bord vers l'autre.
      groupRef.current.position.x = 1.5 - p * 2.6;
      groupRef.current.position.y = -0.6 + p * 1.1;
    }

    // Les lumieres rasantes balayent la surface et ne laissent que des reflets.
    if (grazingLightRef.current) {
      grazingLightRef.current.position.set(
        Math.cos(time * 0.18) * 6,
        1.6 + Math.sin(time * 0.1) * 0.8,
        Math.sin(time * 0.18) * 6
      );
    }
    if (rimLightRef.current) {
      rimLightRef.current.position.set(
        Math.cos(time * 0.18 + Math.PI) * 5,
        -1.2,
        Math.sin(time * 0.18 + Math.PI) * 5
      );
    }
  });

  return (
    <>
      <ambientLight ref={ambientRef} intensity={0} />
      <spotLight
        ref={grazingLightRef}
        intensity={160}
        angle={0.5}
        penumbra={1}
        distance={26}
        color="#dce8f5"
      />
      <spotLight
        ref={rimLightRef}
        intensity={90}
        angle={0.6}
        penumbra={1}
        distance={24}
        color="#7fa6c9"
      />

      {/* Sans quelque chose a reflechir, le verre ne renvoie rien : ces panneaux
          lumineux fournissent les reflets rasants, a la maniere d'un teaser auto. */}
      <Environment resolution={256} frames={1}>
        <color attach="background" args={['#0a0f16']} />
        <Lightformer
          form="rect"
          intensity={9}
          color="#e8f2ff"
          position={[-5, 2, 2]}
          rotation={[0, Math.PI / 2.4, 0]}
          scale={[10, 2.2, 1]}
        />
        <Lightformer
          form="rect"
          intensity={6}
          color="#9fc2e4"
          position={[5, -1.5, 1]}
          rotation={[0, -Math.PI / 2.4, 0]}
          scale={[8, 1.8, 1]}
        />
        <Lightformer
          form="rect"
          intensity={5}
          color="#ffffff"
          position={[0, 5, 1]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={[6, 3, 1]}
        />
        <Lightformer
          form="ring"
          intensity={3}
          color="#ffffff"
          position={[0, 4, -3]}
          scale={3}
        />
      </Environment>

      <group ref={groupRef} scale={model.fit}>
        <primitive object={model.object} />
      </group>
    </>
  );
}

useGLTF.preload('/lentilles.glb');

export default function PreloadedLandingV2() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(() =>
    SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})
  );
  const [dayNight, setDayNight] = useState(0);

  const sectionRefs = useRef(
    SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: React.createRef() }), {})
  ).current;
  // La 3D lit la progression sans passer par un rendu React.
  const s3ProgressRef = useRef(0);

  useEffect(() => {
    logEvent('page_view', 'preloaded_landing_v2');
    dispatch(saveStateBeforeNavigation());

    return () => {
      document.body.style.overflow = '';
      window.scrollTo(0, 0);
    };
  }, [dispatch]);

  useEffect(() => {
    let frame = null;

    const measure = () => {
      frame = null;
      const viewportHeight = window.innerHeight;
      const next = {};

      SECTION_KEYS.forEach((key) => {
        const element = sectionRefs[key].current;
        if (!element) {
          next[key] = 0;
          return;
        }
        const rect = element.getBoundingClientRect();
        const travel = viewportHeight + rect.height;
        // 0 quand la section entre par le bas, 1 quand elle sort par le haut.
        const raw = clamp((viewportHeight - rect.top) / travel, 0, 1);
        next[key] = Math.round(raw * 200) / 200;
      });

      s3ProgressRef.current = next.s3;
      setProgress((current) =>
        SECTION_KEYS.every((key) => current[key] === next[key]) ? current : next
      );
    };

    const onScroll = () => {
      if (frame === null) {
        frame = requestAnimationFrame(measure);
      }
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [sectionRefs]);

  const handleBackToHome = useCallback(() => {
    logEvent('ui_interaction', 'back_to_home_click');
    document.body.style.overflow = '';
    window.scrollTo(0, 0);
    sessionStorage.setItem('saviol-returning-from-new-product', 'true');
    navigate('/');
  }, [navigate]);

  const handleMeetUs = useCallback(() => {
    logEvent('ui_interaction', 'preloaded_v2_meet_us_escrs');
  }, []);

  const isDarkSectionActive = progress.s3 > 0.005 && progress.s3 < 0.995;
  const activeStopIndex = Math.round(dayNight * (DAY_NIGHT_STOPS.length - 1));

  return (
    <div className={styles.container}>
      {/* Le canvas reste en arriere-plan : seule la section 3, transparente, le laisse voir. */}
      <div
        className={styles.canvasContainer}
        // L'emergence est portee par l'eclairage : le canvas reste simplement
        // present pendant toute la section.
        style={{ opacity: range(progress.s3, 0.01, 0.06) * (1 - range(progress.s3, 0.92, 1)) }}
      >
        <Canvas
          camera={{ position: [0, 0, 5.4], fov: 42 }}
          frameloop={isDarkSectionActive ? 'always' : 'demand'}
          gl={{ antialias: true, alpha: true }}
        >
          <Suspense fallback={null}>
            <EmergingObject progressRef={s3ProgressRef} />
          </Suspense>
        </Canvas>
      </div>

      <button className={styles.backButton} onClick={handleBackToHome}>
        ← Back to Swiss Virtual Clinic
      </button>

      <div className={styles.content}>
        {/* ========== HERO — WELCOME ABOARD ========== */}
        <section ref={sectionRefs.hero} className={styles.hero}>
          <div
            className={`${styles.mediaLayer} ${styles.heroMedia}`}
            style={{ ...mediaImage('cabin-hero.jpg'), transform: `scale(${1 + progress.hero * 0.08})` }}
          />
          <div className={styles.heroScrim} />
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Welcome aboard.</h1>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.heroSubtitle}>
              You are among the first surgeons invited to discover what is coming next.
            </p>
            <div className={styles.scrollCue}>
              <span className={styles.scrollArrow} aria-hidden="true">↓</span>
              <span>Scroll to begin your journey.</span>
            </div>
          </div>
        </section>

        {/* ========== SECTION 1 — THE CHALLENGE ========== */}
        <section ref={sectionRefs.s1} className={styles.storySection}>
          <div
            className={`${styles.mediaLayer} ${styles.challengeMedia}`}
            style={{
              ...mediaImage('landscape-challenge.jpg'),
              // La telecabine avance legerement au scroll.
              transform: `scale(1.12) translate3d(${progress.s1 * 3}%, ${progress.s1 * -2.5}%, 0)`
            }}
          />
          <div className={styles.storyScrim} />
          <div className={styles.storyContent} style={{ opacity: reveal(progress.s1) }}>
            <span className={styles.eyebrow}>The Challenge</span>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.storyText}>
              Low-light vision remains one of the greatest unmet expectations in premium
              cataract surgery.
            </p>
          </div>
        </section>

        {/* ========== SECTION 2 — THE OPPORTUNITY ========== */}
        <section ref={sectionRefs.s2} className={styles.storySection}>
          <div
            className={`${styles.mediaLayer} ${styles.opportunityMedia}`}
            style={{
              ...mediaImage('landscape-opportunity.jpg'),
              transform: `scale(1.12) translate3d(${progress.s2 * 2}%, ${progress.s2 * -2}%, 0)`
            }}
          />
          {/* La lumiere devient legerement plus chaude. */}
          <div
            className={styles.warmTint}
            style={{ opacity: 0.15 + easeOut(progress.s2) * 0.45 }}
          />
          <div className={styles.storyScrim} />
          <div className={styles.storyContent} style={{ opacity: reveal(progress.s2) }}>
            <span className={styles.eyebrow}>The Opportunity</span>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.storyText}>
              What if continuous vision and low-light performance no longer required
              compromise?
            </p>
          </div>
        </section>

        {/* ========== SECTION 3 — SOMETHING IS TAKING SHAPE ========== */}
        <section ref={sectionRefs.s3} className={styles.shapeSection}>
          <div className={styles.shapeVignette} />
          <div
            className={styles.shapeContent}
            style={{ opacity: range(progress.s3, 0.45, 0.65) * (1 - range(progress.s3, 0.86, 0.98)) }}
          >
            <h2 className={styles.shapeTitle}>Something is taking shape.</h2>
          </div>
        </section>

        {/* ========== SECTION 4 — SWISS INNOVATION ========== */}
        <section ref={sectionRefs.s4} className={styles.storySection}>
          <div
            className={`${styles.mediaLayer} ${styles.summitMedia}`}
            style={{
              ...mediaImage('summit.jpg'),
              // La telecabine approche du sommet.
              transform: `scale(1.1) translate3d(0, ${6 - progress.s4 * 10}%, 0)`
            }}
          />
          <div className={styles.storyScrim} />
          <div className={styles.storyContent} style={{ opacity: reveal(progress.s4) }}>
            <span className={styles.eyebrow}>Swiss Innovation</span>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.storyText}>
              Engineered in Switzerland.
              <br />
              Designed to inspire.
            </p>
          </div>
        </section>

        {/* ========== SECTION 5 — DAY / NIGHT ========== */}
        <section ref={sectionRefs.s5} className={styles.experienceSection}>
          <div className={styles.experienceStage}>
            {DAY_NIGHT_STOPS.map((stop, index) => (
              <div
                key={stop.id}
                className={`${styles.mediaLayer} ${styles[stop.className]}`}
                style={{
                  ...mediaImage(stop.image),
                  // Le premier calque reste opaque : les suivants se superposent.
                  opacity:
                    index === 0
                      ? 1
                      : range(dayNight, (index - 1) / (DAY_NIGHT_STOPS.length - 1), index / (DAY_NIGHT_STOPS.length - 1))
                }}
              />
            ))}
            <div className={styles.experienceScrim} />
          </div>

          <div className={styles.sliderPanel}>
            {/* Day et Night encadrent la piste sur une seule ligne. */}
            <div className={styles.sliderRow}>
              <span
                className={`${styles.sliderLabel} ${dayNight < 0.5 ? styles.sliderLabelActive : ''}`}
              >
                Day
              </span>
              <input
                className={styles.slider}
                type="range"
                min="0"
                max="1"
                step="0.001"
                value={dayNight}
                onChange={(event) => setDayNight(Number(event.target.value))}
                aria-label="Move from day to night"
              />
              <span
                className={`${styles.sliderLabel} ${dayNight >= 0.5 ? styles.sliderLabelActive : ''}`}
              >
                Night
              </span>
            </div>
            <p className={styles.sliderMoment}>{DAY_NIGHT_STOPS[activeStopIndex].label}</p>
          </div>
        </section>

        {/* ========== SECTION 6 — WHY IT MATTERS ========== */}
        <section ref={sectionRefs.s6} className={styles.mattersSection}>
          <div className={styles.mattersInner}>
            <h2 className={styles.mattersTitle}>Why it matters</h2>
            <span className={`${styles.accentRule} ${styles.accentRuleLeft}`} aria-hidden="true" />
            <ul className={styles.mattersList}>
              {WHY_IT_MATTERS.map((item) => (
                <li key={item} className={styles.mattersItem}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ========== SECTION 7 — SHOW ME THE SCIENCE ========== */}
        <section ref={sectionRefs.s7} className={styles.scienceSection}>
          <div
            className={`${styles.mediaLayer} ${styles.lighthouseMedia}`}
            style={mediaImage('lighthouse.jpg')}
          />
          <div className={styles.lighthouseBeam} aria-hidden="true" />
          <div className={styles.scienceScrim} />
          <div className={styles.scienceContent}>
            <h2 className={styles.scienceTitle}>Show me the science</h2>
            <span className={styles.accentRule} aria-hidden="true" />
            <p className={styles.scienceText}>
              Discover the first clinical evidence exclusively during
            </p>
            <p className={styles.scienceEvent}>Aboard Swiss Vision</p>
            <p className={styles.scienceDate}>11 September</p>
            <p className={styles.scienceNote}>Upon invitation.</p>
            <a
              className={styles.scienceCta}
              href={EVENT_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleMeetUs}
            >
              Meet us at ESCRS
            </a>
          </div>
        </section>

        <footer className={styles.footer}>
          <p>© 2026 SAV-IOL - Swiss Advanced Vision</p>
          <p>
            <a href="mailto:info@sav-iol.com">info@sav-iol.com</a> |
            <a href="tel:+41325665400"> +41 32 566 54 00</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
